import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  IconButton,
  useMediaQuery,
  Backdrop,
  LinearProgress,
  Stack,
} from "@mui/material";
import {
  People,
  DirectionsWalk,
  Elevator,
  Stairs,
  List,
  MonetizationOn,
  LocalGasStation,
  Add,
  Delete,
  Edit,
} from "@mui/icons-material";
import { createRate, getRate, updateRate } from "../../redux/action/rate";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { getCarrier } from "../../redux/action/carrier";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

const Rates: React.FC = () => {
  const theme = useTheme();
  const [rate2Mover, setRate2Mover] = useState("");
  const [rate3Mover, setRate3Mover] = useState("");
  const [rate4Mover, setRate4Mover] = useState("");
  const [rate5Mover, setRate5Mover] = useState("");
  const [rateAdditionalMover, setRateAdditionalMover] = useState("");
  const [elevatorFee, setElevatorFee] = useState("");
  const [extraStairsFee, setExtraStairsFee] = useState("");
  const [longCarryFee, setLongCarryFee] = useState("");
  const [tariffRate, setTariffRate] = useState("");
  const [truckFee, setTruckFee] = useState("");
  const [fuelSurcharge, setFuelSurcharge] = useState("");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const email = localStorage.getItem("email");
  const [carrierId, setCarrierId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [hourlyPackingRate, setHourlyPackingRate] = useState("");
  const [bulkyFeeList, setBulkyFeeList] = useState<
    { item: string; fee: string }[]
  >([{ item: "", fee: "" }]);
  const [editableField, setEditableField] = useState<string | null>(null);
  const [isRateExists, setIsRateExists] = useState(false);
  const addBulkyFeeItem = () => {
    setBulkyFeeList([...bulkyFeeList, { item: "", fee: "" }]);
  };

  const removeBulkyFeeItem = (index: number) => {
    const newList = bulkyFeeList.filter((_, i) => i !== index);
    setBulkyFeeList(newList);
  };

  const handleBulkyFeeChange = (
    index: number,
    field: "item" | "fee",
    value: string
  ) => {
    if (field === "fee" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }
    const newList = [...bulkyFeeList];
    newList[index][field] = value;
    setBulkyFeeList(newList);
  };

  const handleNumericInputChange = (e: any, setter: any) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setter(value);
    }
  };

  const [depositAmount, setDepositAmount] = useState("");
  const [depositPercentage, setDepositPercentage] = useState("");
  const handleSubmit = async () => {
    setIsLoading(true);
    setLoadingMessage("Saving changes. Please wait...");

    const bulkyFeeListObject = bulkyFeeList.reduce(
      (acc: any, { item, fee }) => {
        acc[item] = parseInt(fee, 10);
        return acc;
      },
      {}
    );

    const rateData = {
      carrier_id: carrierId,
      rate_2_mover: parseInt(rate2Mover, 10) || 0,
      rate_3_mover: parseInt(rate3Mover, 10) || 0,
      rate_4_mover: parseInt(rate4Mover, 10) || 0,
      rate_5_mover: parseInt(rate5Mover, 10) || 0,
      rate_additional_mover: parseInt(rateAdditionalMover, 10) || 0,
      elevator_fee: parseInt(elevatorFee, 10) || 0,
      extra_stairs_fee: parseInt(extraStairsFee, 10) || 0,
      long_carry_fee: parseInt(longCarryFee, 10) || 0,
      bulky_fee_list: bulkyFeeListObject,
      tariff_rate: parseInt(tariffRate, 10) || 0,
      fuel_surcharge: parseInt(fuelSurcharge, 10) || 0,
      deposit_amount: depositAmount ? Number(depositAmount) : null,
      deposit_percentage: depositPercentage
        ? parseFloat(depositPercentage)
        : null,
      truck_fee: parseInt(truckFee, 10) || 0,
      hourly_packing_rate: parseInt(hourlyPackingRate, 10) || 0,
    };

    try {
      let response;
      if (isRateExists) {
        response = await dispatch<any>(updateRate(rateData));
      } else {
        response = await dispatch<any>(createRate(rateData));
      }
      const result = unwrapResult(response);
      setRate(result);
      setDepositType("");
      setLoadingMessage("Changes saved successfully!");
      setEditableField(null);
      setIsRateExists(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    } catch (err: any) {
      setLoadingMessage("Failed to save changes.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (email) {
      dispatch<any>(getCarrier(email))
        .then(unwrapResult)
        .then((res: any) => {
          const id = res.id;
          localStorage.setItem("CarrierId", id);
          setCarrierId(id);
        })
        .catch((err: any) => {
          console.log(err, "error-------");
        });
    }
  }, [email]);
  const [rate, setRate] = useState<any>(null);
  useEffect(() => {
    if (carrierId) {
      dispatch<any>(getRate(carrierId))
        .then(unwrapResult)
        .then((res: any) => {
          if (res) {
            setIsRateExists(true);
            setRate(res);
            setRate2Mover(res.rate_2_mover.toString());
            setRate3Mover(res.rate_3_mover.toString());
            setRate4Mover(res.rate_4_mover.toString());
            setRate5Mover(res.rate_5_mover.toString());
            setRateAdditionalMover(res.rate_additional_mover.toString());
            setElevatorFee(res.elevator_fee.toString());
            setExtraStairsFee(res.extra_stairs_fee.toString());
            setLongCarryFee(res.long_carry_fee.toString());
            setHourlyPackingRate(res.hourly_packing_rate.toString());
            setTariffRate(res.tariff_rate.toString());
            setFuelSurcharge(res.fuel_surcharge.toString());
            setTruckFee(res.truck_fee);
            if (res.bulky_fee_list) {
              const bulkyFeeListArray = Object.entries(res.bulky_fee_list).map(
                ([item, fee]) => ({
                  item,
                  fee: (fee as number).toString(),
                })
              );
              setBulkyFeeList(bulkyFeeListArray);
            }
          } else {
            setIsRateExists(false);
          }
        })
        .catch((err: any) => {
          setLoadingMessage("Failed to fetch carrier data.");
        });
    }
  }, [carrierId]);
  const [depositType, setDepositType] = useState("");
  return (
    <Box sx={{ p: 3 }}>
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
          onClick={() => {
            setIsLoading(true);
          }}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography
              fontSize={16}
              fontWeight={500}
              textAlign="center"
              mt={1}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}

      <Typography
        sx={{
          fontWeight: "bold",
          mb: 4,
          fontSize: isMobile ? "1.5rem" : "2rem",
        }}
      >
        Carrier Rates
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
        }}
      >
        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Rate for 2 Movers"
            variant="outlined"
            value={rate2Mover}
            onChange={(e) => handleNumericInputChange(e, setRate2Mover)}
            disabled={isRateExists && editableField !== "rate2Mover"}
            InputProps={{
              startAdornment: (
                <People sx={{ mr: 1, color: theme.palette.primary.main }} />
              ),
              endAdornment: isRateExists && (
                <IconButton
                  onClick={() => setEditableField("rate2Mover")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Rate for 3 Movers"
            variant="outlined"
            value={rate3Mover}
            onChange={(e) => handleNumericInputChange(e, setRate3Mover)}
            disabled={isRateExists && editableField !== "rate3Mover"}
            InputProps={{
              startAdornment: (
                <People sx={{ mr: 1, color: theme.palette.primary.main }} />
              ),
              endAdornment: isRateExists && (
                <IconButton
                  onClick={() => setEditableField("rate3Mover")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Rate for 4 Movers"
            variant="outlined"
            value={rate4Mover}
            onChange={(e) => handleNumericInputChange(e, setRate4Mover)}
            disabled={isRateExists && editableField !== "rate4Mover"}
            InputProps={{
              startAdornment: (
                <People sx={{ mr: 1, color: theme.palette.primary.main }} />
              ),
              endAdornment: isRateExists && (
                <IconButton
                  onClick={() => setEditableField("rate4Mover")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Rate for 5 Movers"
            variant="outlined"
            value={rate5Mover}
            onChange={(e) => handleNumericInputChange(e, setRate5Mover)}
            disabled={isRateExists && editableField !== "rate5Mover"}
            InputProps={{
              startAdornment: (
                <People sx={{ mr: 1, color: theme.palette.primary.main }} />
              ),
              endAdornment: isRateExists && (
                <IconButton
                  onClick={() => setEditableField("rate5Mover")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Rate for Additional Movers"
            variant="outlined"
            value={rateAdditionalMover}
            onChange={(e) =>
              handleNumericInputChange(e, setRateAdditionalMover)
            }
            disabled={isRateExists && editableField !== "rateAdditionalMover"}
            InputProps={{
              startAdornment: (
                <People sx={{ mr: 1, color: theme.palette.primary.main }} />
              ),
              endAdornment: isRateExists && (
                <IconButton
                  onClick={() => setEditableField("rateAdditionalMover")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Elevator Fee"
            variant="outlined"
            value={elevatorFee}
            onChange={(e) => handleNumericInputChange(e, setElevatorFee)}
            disabled={isRateExists && editableField !== "elevatorFee"}
            InputProps={{
              startAdornment: (
                <Elevator sx={{ mr: 1, color: theme.palette.primary.main }} />
              ),
              endAdornment: isRateExists && (
                <IconButton
                  onClick={() => setEditableField("elevatorFee")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Extra Stairs Fee"
            variant="outlined"
            value={extraStairsFee}
            onChange={(e) => handleNumericInputChange(e, setExtraStairsFee)}
            disabled={isRateExists && editableField !== "extraStairsFee"}
            InputProps={{
              startAdornment: (
                <Stairs sx={{ mr: 1, color: theme.palette.primary.main }} />
              ),
              endAdornment: isRateExists && (
                <IconButton
                  onClick={() => setEditableField("extraStairsFee")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Truck Fee"
            variant="outlined"
            value={truckFee}
            onChange={(e) => handleNumericInputChange(e, setTruckFee)}
            disabled={isRateExists && editableField !== "truckFee"}
            InputProps={{
              startAdornment: (
                <LocalShippingIcon
                  sx={{ mr: 1, color: theme.palette.primary.main }}
                />
              ),
              endAdornment: isRateExists && (
                <IconButton
                  onClick={() => setEditableField("truckFee")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>
        {/* <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Hourly Packing Rate"
            variant="outlined"
            value={hourlyPackingRate}
            onChange={(e) => handleNumericInputChange(e, setHourlyPackingRate)}
            disabled={isRateExists && editableField !== "hourlyPackingRate"}
            InputProps={{
              startAdornment: (
                <MonetizationOn
                  sx={{ mr: 1, color: theme.palette.primary.main }}
                />
              ),
              endAdornment: isRateExists && (
                <IconButton
                  onClick={() => setEditableField("hourlyPackingRate")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box> */}
        {/* <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Long Carry Fee"
            variant="outlined"
            value={longCarryFee}
            onChange={(e) => handleNumericInputChange(e, setLongCarryFee)}
            disabled={isRateExists && editableField !== "longCarryFee"}
            InputProps={{
              startAdornment: (
                <MonetizationOn
                  sx={{ mr: 1, color: theme.palette.primary.main }}
                />
              ),
              endAdornment: isRateExists && (
                <IconButton
                  onClick={() => setEditableField("longCarryFee")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box> */}
        {rate?.deposit_amount ? (
          <Typography fontWeight={600}>{`Flat Deposit: $${Number(
            rate?.deposit_amount
          )}`}</Typography>
        ) : rate?.deposit_percentage ? (
          <Typography
            fontWeight={600}
          >{`Deposit Percentage: ${rate?.deposit_percentage}%`}</Typography>
        ) : null}
        <Stack
          alignItems={"center"}
          spacing={1}
          width={"100%"}
          direction={"row"}
        >
          {depositType === "" ? (
            <Button
              onClick={() => {
                setDepositType("flat-rate");
              }}
              variant="contained"
            >
              Flat Rate Deposit
            </Button>
          ) : null}
          {depositType === "" ? (
            <Button
              onClick={() => {
                setDepositType("percentage");
              }}
              variant="contained"
            >
              Percentage Deposit
            </Button>
          ) : null}
          {depositType === "flat-rate" ? (
            <Box
              sx={{
                flex: "1 1 45%",
                minWidth: "240px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <TextField
                fullWidth
                label="Flat Deposit Amount"
                variant="outlined"
                value={depositAmount}
                onChange={(e) => handleNumericInputChange(e, setDepositAmount)}
                disabled={isRateExists && editableField !== "depositAmount"}
                InputProps={{
                  startAdornment: (
                    <MonetizationOn
                      sx={{ mr: 1, color: theme.palette.primary.main }}
                    />
                  ),
                  endAdornment: isRateExists && (
                    <IconButton
                      onClick={() => setEditableField("depositAmount")}
                      sx={{ ml: 1 }}
                    >
                      <Edit />
                    </IconButton>
                  ),
                }}
              />
            </Box>
          ) : null}

          {depositType === "percentage" ? (
            <Box
              sx={{
                flex: "1 1 45%",
                minWidth: "240px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <TextField
                fullWidth
                label="Deposit Percentage"
                variant="outlined"
                value={depositPercentage}
                onChange={(e) =>
                  handleNumericInputChange(e, setDepositPercentage)
                }
                disabled={isRateExists && editableField !== "depositPercentage"}
                InputProps={{
                  startAdornment: (
                    <MonetizationOn
                      sx={{ mr: 1, color: theme.palette.primary.main }}
                    />
                  ),
                  endAdornment: isRateExists && (
                    <IconButton
                      onClick={() => setEditableField("depositPercentage")}
                      sx={{ ml: 1 }}
                    >
                      <Edit />
                    </IconButton>
                  ),
                }}
              />
            </Box>
          ) : null}
        </Stack>
        {/* Bulky Fee List */}
        <Box sx={{ flex: "1 1 100%" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Bulky Fee List
          </Typography>
          {bulkyFeeList.map((item, index) => (
            <Box
              sx={{
                display: "flex",
                gap: 3,
                mb: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
              key={index}
            >
              <Box
                sx={{
                  flex: "1 1 45%",
                  minWidth: "240px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <TextField
                  fullWidth
                  label="Item"
                  variant="outlined"
                  value={item.item}
                  onChange={(e) =>
                    handleBulkyFeeChange(index, "item", e.target.value)
                  }
                  disabled={
                    isRateExists && editableField !== `bulkyItem-${index}`
                  }
                  InputProps={{
                    startAdornment: (
                      <List sx={{ mr: 1, color: theme.palette.primary.main }} />
                    ),
                    endAdornment: isRateExists && (
                      <IconButton
                        onClick={() => setEditableField(`bulkyItem-${index}`)}
                        sx={{ ml: 1 }}
                      >
                        <Edit />
                      </IconButton>
                    ),
                  }}
                />
              </Box>

              <Box
                sx={{
                  flex: "1 1 45%",
                  minWidth: "240px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <TextField
                  fullWidth
                  label="Fee"
                  variant="outlined"
                  value={item.fee}
                  onChange={(e) =>
                    handleBulkyFeeChange(index, "fee", e.target.value)
                  }
                  disabled={
                    isRateExists && editableField !== `bulkyFee-${index}`
                  }
                  InputProps={{
                    startAdornment: (
                      <MonetizationOn
                        sx={{ mr: 1, color: theme.palette.primary.main }}
                      />
                    ),
                    endAdornment: isRateExists && (
                      <IconButton
                        onClick={() => setEditableField(`bulkyFee-${index}`)}
                        sx={{ ml: 1 }}
                      >
                        <Edit />
                      </IconButton>
                    ),
                  }}
                />
              </Box>
            </Box>
          ))}

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={addBulkyFeeItem}
              sx={{ mt: 1 }}
            >
              Add Item
            </Button>

            <IconButton
              color="error"
              onClick={() => removeBulkyFeeItem(bulkyFeeList.length - 1)}
              sx={{ mt: 1 }}
              disabled={bulkyFeeList.length === 0}
            >
              <Delete />
            </IconButton>
          </Box>
        </Box>
        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Tariff Rate"
            variant="outlined"
            value={tariffRate}
            onChange={(e) => handleNumericInputChange(e, setTariffRate)}
            disabled={isRateExists && editableField !== "tariffRate"}
            InputProps={{
              startAdornment: (
                <MonetizationOn
                  sx={{ mr: 1, color: theme.palette.primary.main }}
                />
              ),
              endAdornment: isRateExists && (
                <IconButton
                  onClick={() => setEditableField("tariffRate")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Fuel Surcharge"
            variant="outlined"
            value={fuelSurcharge}
            onChange={(e) => handleNumericInputChange(e, setFuelSurcharge)}
            disabled={isRateExists && editableField !== "fuelSurcharge"}
            InputProps={{
              startAdornment: (
                <LocalGasStation
                  sx={{ mr: 1, color: theme.palette.primary.main }}
                />
              ),
              endAdornment: isRateExists && (
                <IconButton
                  onClick={() => setEditableField("fuelSurcharge")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>{" "}
      </Box>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={{ px: 4, py: 2, width: isMobile ? "100%" : "200px" }}
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};

export default Rates;
